import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const EXPERIENCE_OPTIONS = [
  { value: 'no_experience', label: 'Brand new — first job', sub: 'Never worked before or new to casual shifts' },
  { value: 'some_experience', label: 'I\'ve had a few jobs', sub: 'Some work history, open to anything' },
  { value: 'experienced', label: 'Experienced worker', sub: 'Solid work history, ready to go' },
];

const ROLE_OPTIONS = [
  { value: 'worker', label: 'I want to work', sub: 'Find shifts and earn money' },
  { value: 'employer', label: 'I want to hire', sub: 'Post shifts and find workers' },
];

export default function RegisterPage({ onRegister }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'worker', experience_level: 'some_experience' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return setError('All fields required');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true); setError('');
    try {
      const r = await axios.post('/api/auth/register', form);
      const { token, user } = r.data;
      if (onRegister) onRegister({ token, user });
      const target = user.role === 'admin' ? '/admin' : user.role === 'employer' ? '/employer' : '/worker';
      navigate(target);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <SEOHelmet title="Sign up — ShiftMatch" />

      {/* ── LEFT: Brand Panel ── */}
      <div className="hidden lg:flex w-1/2 gradient-hero relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(16,185,129,0.15)_0%,transparent_60%)]" />
        <div className="absolute top-20 right-20 w-40 h-40 border border-white/5 rounded-full animate-float opacity-10" />
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/5 rounded-xl -rotate-6 animate-float opacity-10" style={{ animationDelay: '-2s' }} />
        <div className="relative text-center">
          <img src="/logo-icon.svg" alt="ShiftMatch" className="w-16 h-16 mx-auto mb-6" />
          <h2 className="font-brand text-3xl font-extrabold text-white mb-3 tracking-tight">Join ShiftMatch</h2>
          <p className="text-white/50 text-sm max-w-xs mx-auto">Free to join. No commitment. Start earning today.</p>
          <div className="mt-8 flex flex-col items-center gap-2 text-white/30 text-[11px]">
            <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400/60" /> No CV needed</span>
            <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400/60" /> Apply in one tap</span>
            <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400/60" /> Paid same day</span>
          </div>
        </div>
      </div>

      {/* ── RIGHT: Register Form ── */}
      <div className="flex-1 overflow-y-auto flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-sm">
          {/* Brand (mobile) */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <img src="/logo-icon.svg" alt="" className="w-7 h-7" />
            <span className="font-brand font-bold text-sm text-neutral-900">ShiftMatch</span>
          </div>

          <h2 className="text-xl font-bold text-neutral-900 mb-1">Create your account</h2>
          <p className="text-sm text-neutral-400 mb-8">Free to join. Start earning today.</p>

          <form onSubmit={handleRegister} className="space-y-4">
            {error && <div className="text-xs text-rose-600 bg-rose-50 px-3 py-2 rounded-lg font-medium">{error}</div>}

            {/* Role toggle */}
            <div>
              <label className="text-[11px] font-semibold text-neutral-600 mb-2 block">I am a...</label>
              <div className="flex gap-2">
                {ROLE_OPTIONS.map(r => (
                  <button key={r.value} type="button"
                    onClick={() => setForm(f => ({ ...f, role: r.value }))}
                    className={`flex-1 px-3 py-2.5 rounded-xl text-[11px] font-semibold text-center transition-all border ${
                      form.role === r.value
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                        : 'bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300'
                    }`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="text-[11px] font-semibold text-neutral-600 mb-1.5 block">Full name</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Your full name"
                className="input w-full px-4 py-3 text-sm" />
            </div>

            {/* Email */}
            <div>
              <label className="text-[11px] font-semibold text-neutral-600 mb-1.5 block">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="your@email.com"
                className="input w-full px-4 py-3 text-sm" />
            </div>

            {/* Password */}
            <div>
              <label className="text-[11px] font-semibold text-neutral-600 mb-1.5 block">Password</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="At least 6 characters"
                className="input w-full px-4 py-3 text-sm" />
            </div>

            {/* Experience level (workers only) */}
            {form.role === 'worker' && (
              <div>
                <label className="text-[11px] font-semibold text-neutral-600 mb-2 block">Experience</label>
                <div className="space-y-1.5">
                  {EXPERIENCE_OPTIONS.map(e => (
                    <button key={e.value} type="button"
                      onClick={() => setForm(f => ({ ...f, experience_level: e.value }))}
                      className={`w-full text-left px-3 py-2.5 rounded-xl transition-all border text-[11px] ${
                        form.experience_level === e.value
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-white border-neutral-200 hover:border-neutral-300'
                      }`}>
                      <span className={`font-semibold ${form.experience_level === e.value ? 'text-emerald-700' : 'text-neutral-700'}`}>{e.label}</span>
                      <span className={`block mt-0.5 ${form.experience_level === e.value ? 'text-emerald-600/70' : 'text-neutral-400'}`}>{e.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button type="submit" disabled={loading}
              className="btn-primary w-full py-3 text-sm">
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <div className="divider" />

          <p className="text-center text-xs text-neutral-400">
            Already have an account?{' '}
            <Link to="/login" className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
